import { useFormik } from 'formik'
import React from 'react'
import ModalComponent from '..'
import { removeElementAtIndex } from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import { useAddCategoryToProductsMutation } from '../../../redux-toolkits/products/products.slice'
import { Product } from '../../../redux-toolkits/products/products.type'
import { Button } from '../../button'
import Icon from '../../icon'
import { TableComponent } from '../../table'
import { SelectProductModal } from './selectProductModal'

interface SelectItemsModalProps {
  open: boolean
  setOpen: (val: boolean) => void
  categoryId?: string
  subcategoryId?: string
  title?: string
  onCloseCallback?: () => void
}

const AddProductsToCategoryModal: React.FC<SelectItemsModalProps> = ({
  open,
  setOpen,
  categoryId,
  subcategoryId,
  title = 'Choose Products',
  onCloseCallback = () => {},
}) => {
  const [addCategory, { isLoading: creating }] =
    useAddCategoryToProductsMutation()

  const { modal, modalOpen, handleModalOpen, handleModalClose, setModalOpen } =
    useModalControl()

  const formik = useFormik({
    initialValues: {
      items: [] as Product[],
    },
    onSubmit: async (values) => {
      if (!values.items.length) return

      const resp = await addCategory({
        categoryId,
        subcategoryId,
        productIds: values.items.map((p) => p._id),
      })

      if (resp.data?.success) {
        setOpen(false)
      }
    },
  })

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      title={title}
      position="right"
      onCloseCallback={() => {
        onCloseCallback()
      }}
    >
      <form onSubmit={formik.handleSubmit} className="flex flex-col !h-full">
        <div className="flex justify-end">
          <Button
            label="Add products"
            type="button"
            className="!w-fit"
            onClick={() => handleModalOpen('selectProductsModal')}
            size="medium"
            primary
          />
        </div>

        <div className="flex-1">
          <TableComponent
            sortable={false}
            headers={undefined}
            rows={
              formik.values.items.length
                ? formik.values.items.map((product, idx) => {
                    return {
                      id: `${product.name}-${product._id}`,
                      content: [
                        <div
                          key={`${idx}-name`}
                          className="w-full flex items-center justify-between gap-4"
                        >
                          <div className="max-w-fit text-left hover:underline flex items-center gap-4">
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="aspect-square w-[40px] rounded-md"
                            />
                            {product.name}
                          </div>

                          <button
                            className="max-w-fit"
                            type="button"
                            onClick={() => {
                              formik.setFieldValue(
                                'items',
                                removeElementAtIndex(
                                  [...formik.values.items],
                                  idx,
                                ),
                              )
                            }}
                          >
                            <Icon
                              id="bin"
                              width={16}
                              height={16}
                              className="text-danger"
                            />
                          </button>
                        </div>,
                      ],
                    }
                  })
                : []
            }
            name="selected-products-table"
            loading={false}
            isEmpty={[undefined, 0].includes(formik.values.items.length)}
            currentPage={1}
            totalDataCount={formik.values.items.length}
            pageLimit={formik.values.items.length}
            onPageChange={(page) => {}}
          />
        </div>

        <Button
          label="Proceed"
          type="submit"
          className="mt-10 w-full"
          disabled={creating || formik.values.items.length === 0}
          loading={creating}
          primary
        />

        {modalOpen && (
          <SelectProductModal
            open={modalOpen && modal === 'selectProductsModal'}
            setOpen={setModalOpen}
            onCloseCallback={() => {
              handleModalClose()
            }}
            selectedProducts={formik.values.items}
            onSuccessCallback={(products: Product[]) => {
              formik.setFieldValue('items', [
                ...formik.values.items,
                ...products.filter(
                  (p) => !formik.values.items.map((i) => i._id).includes(p._id),
                ),
              ])

              handleModalClose()
            }}
          />
        )}
      </form>
    </ModalComponent>
  )
}

export default AddProductsToCategoryModal
